import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { templateBySlugQueryOptions, type SiteTemplate } from "@/lib/templates.queries";
import { TemplateRenderer } from "@/components/site/TemplateRenderer";
import { useHashArrival } from "@/lib/hash-arrival";
import { PageHero } from "@/components/site/PageHero";
import { SLUG_TO_PAGE } from "@/lib/page-backgrounds";

/**
 * Retired template pages and the real page each one forwards to.
 *
 * These four were early template copies of pages that now have routes of
 * their own (and "overview" was design-phase text). They get switched off
 * (unpublished) in Admin > Templates, but their addresses were in sitemap.xml
 * and may be linked or bookmarked, so they forward instead of showing "Page
 * not found".
 *
 * Only consulted when the template is NOT available (unpublished or deleted):
 * while a template is published its page renders as usual, so switching it
 * back on in the admin restores the page with no code change.
 */
const RETIRED_TEMPLATE_FORWARDS = new Map<string, "/welcome" | "/board" | "/sponsors">([
  ["home", "/welcome"],
  ["board", "/board"],
  ["sponsors", "/sponsors"],
  ["overview", "/welcome"],
]);

export const Route = createFileRoute("/p/$slug")({
  loader: async ({ context, params }) => {
    const tpl = (await context.queryClient.ensureQueryData(
      templateBySlugQueryOptions(params.slug),
    )) as SiteTemplate | null;
    if (!tpl || !tpl.is_published) {
      // The router matches paths case-insensitively, so /p/Home arrives here too.
      const forwardTo = RETIRED_TEMPLATE_FORWARDS.get(params.slug.toLowerCase());
      if (forwardTo) throw redirect({ to: forwardTo, replace: true });
      throw notFound();
    }
    // Fed to head() so prerendered snapshots carry the editor's real SEO text
    // instead of the raw slug.
    return {
      title: tpl.seo_title || tpl.name || params.slug,
      description: tpl.seo_description || tpl.description,
    };
  },
  head: ({ loaderData, params }) => ({
    meta: [
      { title: `${loaderData?.title ?? params.slug} — Medical Insight` },
      ...(loaderData?.description
        ? [{ name: "description", content: loaderData.description }]
        : []),
    ],
  }),
  component: TemplatePage,
});

function TemplatePage() {
  return (
    <Suspense
      fallback={
        <div className="mx-auto max-w-7xl px-4 py-16 text-sm text-muted-foreground">Loading…</div>
      }
    >
      <TemplateBody />
    </Suspense>
  );
}

function TemplateBody() {
  const { slug } = Route.useParams();
  const { data: tpl } = useSuspenseQuery(templateBySlugQueryOptions(slug));
  // A link to a section (#membership) lands below the top bar once the page has settled.
  useHashArrival();
  if (!tpl) return null;

  const hasHero = !!(tpl.hero_kicker || tpl.hero_title || tpl.hero_subtitle || tpl.hero_image_path);

  const pageKey = SLUG_TO_PAGE[slug];

  return (
    <div className="bg-background">
      {hasHero && pageKey && (
        <PageHero
          page={pageKey}
          /* An editor-chosen image wins over the per-direction default; without
             this, setting hero_image_path switched the hero on but never showed. */
          bgSrc={tpl.hero_image_path ?? undefined}
          kicker={tpl.hero_kicker ?? undefined}
          title={tpl.hero_title ?? ""}
          subtitle={tpl.hero_subtitle ?? undefined}
        />
      )}
      {hasHero && !pageKey && (
        <section className="border-b bg-gradient-to-b from-secondary/40 to-background">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
            {tpl.hero_kicker && (
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                {tpl.hero_kicker}
              </div>
            )}
            {tpl.hero_title && (
              <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
                {tpl.hero_title}
              </h1>
            )}
            {tpl.hero_subtitle && (
              <p className="mt-4 max-w-3xl text-base text-muted-foreground sm:text-lg">
                {tpl.hero_subtitle}
              </p>
            )}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 pt-10 pb-16 sm:pt-14 sm:pb-20">
        <TemplateRenderer body={tpl.body_md ?? ""} />
      </section>
    </div>
  );
}
